import React from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SuperAdminTenantProvider } from './context/SuperAdminTenantContext';
import SuperAdminBar from './components/Layout/SuperAdminBar';
import Login from './components/Auth/Login';
import Dashboard from './pages/Dashboard';
import CustomerList from './components/Customers/CustomerList';
import CustomerDetail from './components/Customers/CustomerDetail';
import CampaignsPage from './pages/campaigns/CampaignsPage';
import CampaignBuilderPage from './pages/campaigns/CampaignBuilderPage';
import CampaignDetailPage from './pages/campaigns/CampaignDetailPage';
import CampaignSegmentsPage from './pages/campaigns/CampaignSegmentsPage';
import LeadList from './components/Leads/LeadList';
import LeadDetail from './components/Leads/LeadDetail';
import LeadForm from './components/Leads/LeadForm';
import ContactList from './components/Contacts/ContactList';
import ContactDetail from './components/Contacts/ContactDetail';
import OrganizationList from './components/Organizations/OrganizationList';
import DealList from './components/Deals/DealList';
import DealDetail from './components/Deals/DealDetail';
import TasksPage from './pages/TasksPage';
import AdminUsers from './pages/AdminUsers';
import DevicesPage from './pages/DevicesPage';
import SimCallsPage from './pages/SimCallsPage';
import CallReportsPage from './pages/CallReportsPage';
import LiveMonitorPage from './pages/LiveMonitorPage';
import TenantsPage from './pages/TenantsPage';
import PermissionsPage from './pages/PermissionsPage';
import AssignmentPage from './pages/AssignmentPage';
import IndiaMartIntegrationPage from './pages/IndiaMartIntegrationPage';
import AnalyzerConsolePage from './pages/AnalyzerConsolePage';
import AbReviewConsolePage from './pages/AbReviewConsolePage';
import TenantEmailTogglesPage from './pages/TenantEmailTogglesPage';
import AiUsagePage from './pages/AiUsagePage';
import TeleobiConfigPage from './pages/TeleobiConfigPage';
import AgentWhatsAppPage from './pages/AgentWhatsAppPage';
import AttendancePage from './pages/AttendancePage';
import RelationshipProposalsPage from './pages/RelationshipProposalsPage';
import ComplaintsPage from './pages/ComplaintsPage';
import Sidebar from './components/Layout/Sidebar';
import Topbar from './components/Layout/Topbar';

const PrivateRoute: React.FC<{ path: string; component: React.ComponentType; exact?: boolean }> = ({
  component: Component, ...rest
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <div className="spinner">Loading…</div>;
  return (
    <Route
      {...rest}
      render={() =>
        isAuthenticated ? (
          <div className="layout">
            <Sidebar />
            <div className="main-wrap">
              <Topbar />
              <SuperAdminBar />
              <div className="page-content">
                <Component />
              </div>
            </div>
          </div>
        ) : (
          <Redirect to="/login" />
        )
      }
    />
  );
};

const AdminRoute: React.FC<{ path: string; component: React.ComponentType; exact?: boolean }> = ({
  component: Component, ...rest
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <div className="spinner">Loading…</div>;
  return (
    <Route
      {...rest}
      render={() => {
        if (!isAuthenticated) return <Redirect to="/login" />;
        if (user?.role !== 'tenant_admin' && user?.role !== 'super_admin') return <Redirect to="/dashboard" />;
        return (
          <div className="layout">
            <Sidebar />
            <div className="main-wrap">
              <Topbar />
              <SuperAdminBar />
              <div className="page-content">
                <Component />
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

const SuperAdminRoute: React.FC<{ path: string; component: React.ComponentType; exact?: boolean }> = ({
  component: Component, ...rest
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  if (isLoading) return <div className="spinner">Loading…</div>;
  return (
    <Route
      {...rest}
      render={() => {
        if (!isAuthenticated) return <Redirect to="/login" />;
        if (user?.role !== 'super_admin') return <Redirect to="/dashboard" />;
        return (
          <div className="layout">
            <Sidebar />
            <div className="main-wrap">
              <Topbar />
              <SuperAdminBar />
              <div className="page-content">
                <Component />
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <SuperAdminTenantProvider>
      <Router>
        <Switch>
          <Route path="/login" component={Login} />
          <Route path="/register" render={() => <Redirect to="/login" />} />
          <PrivateRoute path="/dashboard"      component={Dashboard}        exact />
          <PrivateRoute path="/leads/new"      component={LeadForm}         exact />
          <PrivateRoute path="/leads/:id/edit" component={LeadForm}         exact />
          <PrivateRoute path="/leads/relationship-proposals" component={RelationshipProposalsPage} exact />
          <PrivateRoute path="/complaints" component={ComplaintsPage} exact />
          <PrivateRoute path="/leads/:id"      component={LeadDetail}       exact />
          <PrivateRoute path="/leads"          component={LeadList}         exact />
          <PrivateRoute path="/contacts/:id"   component={ContactDetail}    exact />
          <PrivateRoute path="/contacts"       component={ContactList}      exact />
          <PrivateRoute path="/organizations"  component={OrganizationList} exact />
          <PrivateRoute path="/deals"          component={DealList}         exact />
          <PrivateRoute path="/deals/:id"      component={DealDetail}       exact />
          <PrivateRoute path="/tasks"          component={TasksPage}        exact />
          <PrivateRoute path="/customers/:id"        component={CustomerDetail}      exact />
          <PrivateRoute path="/customers"           component={CustomerList}        exact />
          <PrivateRoute path="/campaigns/new"       component={CampaignBuilderPage} exact />
          <PrivateRoute path="/campaigns/:id/edit"  component={CampaignBuilderPage} exact />
          <PrivateRoute path="/campaigns/:id"       component={CampaignDetailPage}  exact />
          <PrivateRoute path="/campaigns/segments"  component={CampaignSegmentsPage} exact />
          <PrivateRoute path="/campaigns"           component={CampaignsPage}       exact />
          <PrivateRoute path="/settings/devices" component={DevicesPage}      exact />
          <PrivateRoute path="/sim-calls"        component={SimCallsPage}     exact />
          <PrivateRoute path="/call-reports"     component={CallReportsPage}  exact />
          <PrivateRoute path="/live-monitor"     component={LiveMonitorPage}  exact />
          <AdminRoute      path="/admin/users"       component={AdminUsers}      exact />
          <AdminRoute      path="/admin/permissions" component={PermissionsPage} exact />
          <PrivateRoute    path="/admin/assignment"  component={AssignmentPage}  exact />
          <PrivateRoute    path="/attendance"         component={AttendancePage}   exact />
          <AdminRoute      path="/admin/integrations/indiamart" component={IndiaMartIntegrationPage} exact />
          <SuperAdminRoute path="/super/tenants"     component={TenantsPage}     exact />
          <SuperAdminRoute path="/super/analyzer"    component={AnalyzerConsolePage} exact />
          <SuperAdminRoute path="/super/ab-review"   component={AbReviewConsolePage} exact />
          <SuperAdminRoute path="/super/tenant-email" component={TenantEmailTogglesPage} exact />
          <SuperAdminRoute path="/super/ai-usage"    component={AiUsagePage} exact />
          <SuperAdminRoute path="/super/teleobi"     component={TeleobiConfigPage}   exact />
          <SuperAdminRoute path="/super/agent-whatsapp" component={AgentWhatsAppPage} exact />
          <Route exact path="/" render={() => <Redirect to="/dashboard" />} />
        </Switch>
      </Router>
      </SuperAdminTenantProvider>
    </AuthProvider>
  );
};

export default App;
